import { useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { DonkeyMascot } from "@/components/DonkeyMascot";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, formatApiError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate(params.get("next") || location.state?.from || "/dashboard");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-donkey-bg grain flex flex-col">
      <div className="p-6"><Logo /></div>
      <div className="flex-1 flex items-center justify-center px-5 pb-16">
        <div className="w-full max-w-md card-donkey rounded-2xl p-8 animate-fade-up">
          <DonkeyMascot size={96} alt="" className="mx-auto mb-4 opacity-95 drop-shadow-[0_0_40px_rgba(249,115,22,0.22)]" />
          <h1 className="font-heading font-bold text-2xl text-donkey-warm">Log in</h1>
          <p className="text-sm text-donkey-grey mt-2">The donkey remembers you. Probably.</p>
          <form onSubmit={submit} className="mt-7 space-y-5">
            <div>
              <Label htmlFor="email" className="text-donkey-warm/90">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 bg-donkey-bg border-white/15 text-donkey-warm" data-testid="login-email" />
            </div>
            <div className="relative">
              <div className="flex justify-between items-center">
                <Label htmlFor="password" className="text-donkey-warm/90">Password</Label>
                <Link to="/reset-password" className="text-xs text-donkey-orange hover:underline">Forgot?</Link>
              </div>
              <Input id="password" type={showPw ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 bg-donkey-bg border-white/15 text-donkey-warm pr-10" data-testid="login-password" />
              <button type="button" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Hide password" : "Show password"}
                aria-pressed={showPw} data-testid="login-password-toggle"
                className="absolute right-3 top-9 text-donkey-grey hover:text-donkey-warm">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {err && <p className="text-sm text-donkey-red" data-testid="login-error">{err}</p>}
            <Button type="submit" disabled={loading}
              className="w-full bg-donkey-orange text-donkey-black hover:bg-donkey-orange/90 font-semibold h-11"
              data-testid="login-submit">
              {loading ? "Checking…" : "Log in"}
            </Button>
          </form>
          <p className="text-sm text-donkey-grey mt-6 text-center">
            No account? <Link to="/register" className="text-donkey-orange underline hover:no-underline">Create one</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
